import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pb from '../pocketbase';
import { FaEdit, FaCalendarPlus, FaCamera, FaFileInvoice, FaCar, FaTrash, FaPlus, FaGift, FaWhatsapp, FaShareAlt, FaCopy, FaTicketAlt, FaStore, FaStar, FaExclamationTriangle, FaDownload, FaColumns, FaArrowsAltH, FaMoon, FaSun, FaMapMarkerAlt, FaCalendarDay, FaClock, FaCheckCircle, FaHistory, FaHourglassHalf, FaTools, FaCoins, FaInfoCircle, FaTimes, FaChevronRight, FaCalendarAlt, FaShieldAlt } from 'react-icons/fa'; // Icons
import VehicleInspector from '../components/VehicleInspector';
import ComparisonSlider from '../components/ComparisonSlider';
import jsPDF from 'jspdf';
import './ProfilePage.css';

const ProfilePage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(pb.authStore.isValid ? pb.authStore.model : null);
    const [appointments, setAppointments] = useState([]);
    const [isInitialCheckDone, setIsInitialCheckDone] = useState(false);

    // Feature 1: Vehicles State
    const [vehicles, setVehicles] = useState([]);
    const [newVehicle, setNewVehicle] = useState({ marque: '', modele: '', categorie: 'citadine', immatriculation: '' });
    const [isAddingVehicle, setIsAddingVehicle] = useState(false);

    // Feature 2: Photos Modal
    const [selectedPhotoApt, setSelectedPhotoApt] = useState(null);


    // Feature VIP Card
    const [showVIPCard, setShowVIPCard] = useState(false);

    // Feature 5: Edit Profile State
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ name: '', email: '', telephone: '' });

    // Comparison Mode
    const [compareMode, setCompareMode] = useState('slider'); // 'slider' or 'grid'

    // Tab Management
    const [activeTab, setActiveTab] = useState('dashboard');
    const [currentPairIndex, setCurrentPairIndex] = useState(0);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [garageAppointmentsFilter, setGarageAppointmentsFilter] = useState(null); // To filter by vehicle plaque
    const [editingVehicleId, setEditingVehicleId] = useState(null);
    const [editVehicleData, setEditVehicleData] = useState({ marque: '', modele: '', immatriculation: '', categorie: '' });
    const userMenuRef = useRef(null);

    // Theme Management
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    useEffect(() => {
        document.body.className = theme === 'dark' ? 'dark-mode' : 'light-mode';
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    // Close Dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auth Listener
    useEffect(() => {
        const unsub = pb.authStore.onChange((token, model) => {
            setUser(pb.authStore.isValid ? model : null);
        });
        return unsub;
    }, []);

    useEffect(() => {
        // Initial check
        if (!pb.authStore.isValid) {
            console.warn("ProfilePage: Auth invalid, redirecting to home.");
            navigate('/');
            return;
        }
        
        setIsInitialCheckDone(true);

        if (user) setEditData({ name: user.name || '', email: user.email || '', telephone: user.telephone || '' });

        loadData();
        loadVehicles();

        const unsubscribeApt = pb.collection('rendez_vous').subscribe('*', handleAptEvent);
        return () => {
            unsubscribeApt.then(unsub => unsub());
        };
    }, [user?.id, navigate]);

    const loadData = async () => {
        try {
            const records = await pb.collection('rendez_vous').getList(1, 100, {
                filter: `client_email = "${user.email}"`,
                sort: '-date_heure',
            });
            setAppointments(records.items);
        } catch (err) { console.error(err); }
    };


    const loadVehicles = async () => {
        try {
            const records = await pb.collection('vehicules').getList(1, 50, {
                filter: `user = "${user.id}"`,
                sort: '-created',
            });
            setVehicles(records.items);
        } catch (err) { console.error("Garage Error", err); }
    };

    const handleAptEvent = (e) => {
        if (e.record.client_email === user.email) {
            if (e.action === 'create') setAppointments((p) => [e.record, ...p]);
            else if (e.action === 'update') setAppointments((p) => p.map((i) => (i.id === e.record.id ? e.record : i)));
            else if (e.action === 'delete') setAppointments((p) => p.filter((i) => i.id !== e.record.id));
        }
    };

    const formatPlaque = (val) => {
        const cleaned = val.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 7);
        let formatted = cleaned;
        if (cleaned.length > 2) formatted = cleaned.slice(0, 2) + '-' + cleaned.slice(2);
        if (cleaned.length > 5) formatted = formatted.slice(0, 6) + '-' + formatted.slice(6);
        return formatted;
    };

    const getVehicleStats = (plaque) => {
        if (!appointments) return { count: 0, lastDate: null };
        const cleanPlaque = plaque.replace(/\s|-/g, '').toUpperCase();
        const filtered = appointments.filter(apt => {
            const aptPlaque = apt.options?.match(/\[Plaque:\s?([^\]]+)\]/i)?.[1]?.replace(/\s|-/g, '').toUpperCase();
            return aptPlaque === cleanPlaque;
        });
        
        return {
            count: filtered.length,
            lastDate: filtered.length > 0 ? filtered[0].date_heure : null
        };
    };

    const handleStartEditVehicle = (veh) => {
        setEditingVehicleId(veh.id);
        setEditVehicleData({ 
            marque: veh.marque, 
            modele: veh.modele, 
            immatriculation: formatPlaque(veh.immatriculation), 
            categorie: veh.categorie 
        });
    };

    const handleSaveEditVehicle = async (id) => {
        try {
            const cleanPlaque = editVehicleData.immatriculation.replace(/\s|-/g, '').toUpperCase();
            const updated = await pb.collection('vehicules').update(id, {
                ...editVehicleData,
                immatriculation: cleanPlaque
            });
            setVehicles(prev => prev.map(v => v.id === id ? updated : v));
            setEditingVehicleId(null);
            alert("Informations mises à jour !");
        } catch (err) { alert("Erreur lors de la mise à jour"); }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            const updatedUser = await pb.collection('users').update(user.id, {
                ...editData,
                emailVisibility: true
            });
            setUser(updatedUser);
            setIsEditing(false);
            alert("Profil mis à jour !");
        } catch (err) { alert("Erreur maj profil"); }
    };

    const handleAddVehicle = async (e) => {
        e.preventDefault();
        try {
            const cleanPlaque = newVehicle.immatriculation.replace(/\s/g, '').toUpperCase();
            const data = { ...newVehicle, immatriculation: cleanPlaque, user: user.id };
            const record = await pb.collection('vehicules').create(data);
            setVehicles([record, ...vehicles]);
            setNewVehicle({ marque: '', modele: '', categorie: 'citadine', immatriculation: '' });
            setIsAddingVehicle(false);
            alert("Véhicule ajouté au garage !");
        } catch (err) { alert("Erreur ajout véhicule"); }
    };

    const handleDeleteVehicle = async (id) => {
        if (!window.confirm("Supprimer ce véhicule ?")) return;
        try {
            await pb.collection('vehicules').delete(id);
            setVehicles(vehicles.filter(v => v.id !== id));
        } catch (err) { alert("Erreur suppression"); }
    };

    const getPhotoUrl = (apt, filename) => {
        if (!filename) return '';
        return `${process.env.REACT_APP_PB_URL || 'http://127.0.0.1:8090'}/api/files/rendez_vous/${apt.id}/${filename}`;
    };

    const generatePhotoReport = async (apt) => {
        const doc = new jsPDF();

        // Header
        doc.setFontSize(22);
        doc.setTextColor(37, 99, 235);
        doc.text("ROULEZPROPRE", 105, 20, { align: 'center' });

        doc.setFontSize(16);
        doc.setTextColor(0);
        doc.text("Rapport de Transformation & Soin", 105, 30, { align: 'center' });

        // Client/Vehicle Info
        doc.setFontSize(12);
        doc.text(`Client: ${apt.client_nom}`, 20, 50);
        doc.text(`Prestation: ${apt.prestation}`, 20, 58);
        doc.text(`Date: ${new Date(apt.date_heure).toLocaleDateString()}`, 20, 66);
        doc.text(`Véhicule: ${apt.client_nom}`, 20, 74);

        // Photos
        if (apt.photo_avant && apt.photo_apres) {
            doc.setFontSize(14);
            doc.text("TRANSFORMATION", 20, 95);

            // Add Avant
            doc.setFontSize(10);
            doc.text("AVANT", 45, 105);
            doc.addImage(getPhotoUrl(apt, apt.photo_avant), 'JPEG', 20, 110, 80, 60);

            // Add Apres
            doc.text("APRÈS", 145, 105);
            doc.addImage(getPhotoUrl(apt, apt.photo_apres), 'JPEG', 110, 110, 80, 60);
        }

        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text("Merci pour votre confiance. Roulez avec style, roulez propre.", 105, 280, { align: 'center' });

        doc.save(`Rapport_RoulezPropre_${apt.id.slice(0, 5)}.pdf`);
    };

    const pending = appointments.filter((a) => a.statut === 'en_attente');
    const confirmed = appointments.filter((a) => a.statut === 'valide' || a.statut === 'en_cours');
    const history = appointments.filter((a) => a.statut === 'termine' || a.statut === 'refuse' || a.statut === 'annule');


    const getNextAppointment = () => {
        const sortedConfirmed = [...confirmed].sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure));
        const now = new Date();
        return sortedConfirmed.find(a => new Date(a.date_heure) >= now);
    };

    const getCountdown = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = date - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (date.toDateString() === now.toDateString()) return "Aujourd'hui";
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (date.toDateString() === tomorrow.toDateString()) return "Demain";
        
        if (diffDays > 0) return `Dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
        if (diffHours > 0) return `Dans ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
        return "Imminent";
    };

    const renderCard = (apt) => {
        const dateObj = new Date(apt.date_heure);
        const dateStr = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        const timeStr = dateObj.getHours() + 'h' + (dateObj.getMinutes() > 0 ? dateObj.getMinutes() : '00');

        return (
            <div key={apt.id} className="apt-card premium-card-detailed">
                <div className={`apt-card-top-status ${apt.statut}`}>
                    <div className="status-label">
                        {apt.statut === 'en_attente' && <><FaHourglassHalf /> EN ATTENTE DE VALIDATION</>}
                        {(apt.statut === 'valide' || apt.statut === 'en_cours') && <><FaCheckCircle /> RÉSERVATION CONFIRMÉE</>}
                        {apt.statut === 'termine' && <><FaCheckCircle /> PRESTATION TERMINÉE</>}
                        {apt.statut === 'refuse' && <><FaTimes /> RÉSERVATION REFUSÉE</>}
                        {apt.statut === 'annule' && <><FaTimes /> RÉSERVATION ANNULÉE</>}
                    </div>
                    <div className="apt-id">ID: {apt.id.slice(0, 8).toUpperCase()}</div>
                </div>

                <div className="apt-card-main-content">
                    <div className="apt-main-header">
                        <div className="service-main-title">
                            <FaTools className="icon-blue" />
                            <div>
                                <h3>{apt.prestation}</h3>
                                <p className="duration-tag"><FaClock /> Durée estimée : {Math.floor((apt.duree || 90) / 60)}h{(apt.duree || 90) % 60 > 0 ? (apt.duree || 90) % 60 : ''}</p>
                            </div>
                        </div>
                        <div className="price-total-badge">
                            {apt.prix || apt.total_prix || 0}€
                        </div>
                    </div>

                    <div className="apt-details-grid">
                        <div className="detail-col">
                            <div className="detail-item-title"><FaCalendarDay /> DATE & HEURE</div>
                            <div className="detail-item-value">{dateStr} à {timeStr}</div>
                        </div>
                        <div className="detail-col">
                            <div className="detail-item-title"><FaMapMarkerAlt /> LIEU D'INTERVENTION</div>
                            <div className="detail-item-value address-val">{apt.adresse || 'Lieu à confirmer'}</div>
                        </div>
                    </div>

                    <div className="apt-technical-details">
                        <div className="tech-section">
                            <div className="tech-title">DÉTAILS DES VÉHICULES / PRESTATIONS</div>
                            <div className="tech-content">
                                {apt.options ? apt.options.split(' | ').map((opt, i) => (
                                    <div key={i} className="tech-option-line">
                                        <FaCar className="bullet" /> {opt}
                                    </div>
                                )) : <p>Aucun détail supplémentaire</p>}
                            </div>
                        </div>
                        
                        <div className="financial-breakdown">
                            <div className="breakdown-row">
                                <span>Forfait de base</span>
                                <span>{(apt.prix || 0) - (apt.frais_deplacement || 0) - (apt.total_options || 0)}€</span>
                            </div>
                            {apt.total_options > 0 && (
                                <div className="breakdown-row">
                                    <span>Options sélectionnées</span>
                                    <span>+{apt.total_options}€</span>
                                </div>
                            )}
                            {apt.frais_deplacement > 0 && (
                                <div className="breakdown-row">
                                    <span>Frais de déplacement</span>
                                    <span>+{apt.frais_deplacement}€</span>
                                </div>
                            )}
                            {apt.remise_montant > 0 && (
                                <div className="breakdown-row discount">
                                    <span>Remise commerciale</span>
                                    <span>-{apt.remise_montant}€</span>
                                </div>
                            )}
                            <div className="breakdown-total">
                                <span>Total Payé / À payer</span>
                                <span>{apt.prix || 0}€</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="apt-card-bottom-actions">
                    {apt.statut === 'termine' && (apt.photo_avant || apt.photo_apres || (apt.photos_avant_apres && apt.photos_avant_apres.length > 0)) && (
                        <button className="btn-action-premium primary" onClick={() => setSelectedPhotoApt(apt)}>
                            <FaCamera /> VOIR LA TRANSFORMATION
                        </button>
                    )}
                    {apt.statut === 'en_attente' && (
                        <p className="status-note"><FaInfoCircle /> Notre équipe valide votre créneau sous peu.</p>
                    )}
                    {(apt.statut === 'valide' || apt.statut === 'en_cours') && (
                        <div className="live-status-indicator">
                            <span className="pulse-blue"></span> Intervention planifiée
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!isInitialCheckDone) {
        return <div className="profile-loading" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', background: '#0f172a' }}>Vérification de la session...</div>;
    }

    if (!user) return null;

    return (
        <div className="profile-page-wrapper">
            {/* Sidebar Navigation - Desktop */}
            <aside className="profile-sidebar">
                <div className="sidebar-header">
                    <div className="sidebar-logo">RP</div>
                    <div className="sidebar-user-brief">
                        <div className="mini-avatar">
                            {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                        </div>
                        <div className="user-text">
                            <h4>{user.name || 'Client'}</h4>
                            <span>{user.email}</span>
                        </div>
                    </div>
                </div>

                <nav className="sidebar-nav">
                    <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
                        <FaColumns /> Tableau de bord
                    </button>
                    <button className={activeTab === 'appointments' ? 'active' : ''} onClick={() => setActiveTab('appointments')}>
                        <FaCalendarPlus /> Rendez-vous
                    </button>
                    <button className={activeTab === 'garage' ? 'active' : ''} onClick={() => setActiveTab('garage')}>
                        <FaCar /> Mon Garage
                    </button>
                    <button className={activeTab === 'documents' ? 'active' : ''} onClick={() => setActiveTab('documents')}>
                        <FaFileInvoice /> Documents
                    </button>
                    <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
                        <FaEdit /> Profil
                    </button>
                </nav>

                <div className="sidebar-footer">
                    <button onClick={() => { pb.authStore.clear(); navigate('/'); }} className="logout-btn">
                        Déconnexion
                    </button>
                </div>
            </aside>

            {/* Bottom Nav - Mobile */}
            <nav className="mobile-bottom-nav">
                <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
                    <FaColumns />
                    <span>Accueil</span>
                </button>
                <button className={activeTab === 'appointments' ? 'active' : ''} onClick={() => setActiveTab('appointments')}>
                    <FaCalendarPlus />
                    <span>RDV</span>
                </button>
                <button className={activeTab === 'garage' ? 'active' : ''} onClick={() => setActiveTab('garage')}>
                    <FaCar />
                    <span>Garage</span>
                </button>
                <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
                    <FaEdit />
                    <span>Moi</span>
                </button>
            </nav>

            <div className="profile-main-container">
                {/* Client Header / Command Center */}
                <header className="client-header">
                    <div className="header-left">
                        <span className="current-section-title">
                            {activeTab === 'dashboard' && 'Tableau de bord'}
                            {activeTab === 'appointments' && 'Mes Rendez-vous'}
                            {activeTab === 'garage' && 'Mon Garage'}
                            {activeTab === 'documents' && 'Mes Documents'}
                            {activeTab === 'profile' && 'Mon Profil'}
                        </span>
                    </div>
                    <div className="header-right">
                        <button onClick={toggleTheme} className="theme-toggle-btn" title="Changer le thème">
                            {theme === 'light' ? <FaMoon /> : <FaSun style={{ color: '#f59e0b' }} />}
                        </button>

                        <div className="nav-icon-item user-item" style={{ position: 'relative' }} ref={userMenuRef}>
                            <button
                                className="user-btn logged-in-avatar"
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                title="Mon compte"
                            >
                                {user.name ? user.name.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                            </button>

                            {isUserMenuOpen && (
                                <div className="user-dropdown-menu">
                                    {user.role === 'admin' && (
                                        <Link
                                            to="/admin"
                                            className="user-dropdown-item"
                                            onClick={() => setIsUserMenuOpen(false)}
                                            style={{ color: '#f97316', fontWeight: 'bold' }}
                                        >
                                            Espace Admin
                                        </Link>
                                    )}
                                    <Link
                                        to="/profile"
                                        className="user-dropdown-item"
                                        onClick={() => setIsUserMenuOpen(false)}
                                    >
                                        Mon compte
                                    </Link>
                                    <button
                                        onClick={() => {
                                            pb.authStore.clear();
                                            navigate('/');
                                        }}
                                        className="user-dropdown-item logout-btn-drop"
                                    >
                                        Se déconnecter
                                    </button>
                                </div>
                            )}
                        </div>

                        <Link to="/" className="back-site-btn">
                            <FaShareAlt style={{ transform: 'rotate(180deg)' }} /> Quitter
                        </Link>
                    </div>
                </header>

                <div className="content-scroll-area">
                    {/* DASHBOARD TAB */}
                    {activeTab === 'dashboard' && (
                        <div className="tab-section dashboard-tab fade-in">
                            <h2>Tableau de bord</h2>

                            {/* FOCUS PROCHAIN RDV - Bandeau Majeur */}
                            {(() => {
                                const nextApt = getNextAppointment();
                                if (!nextApt) return null;
                                const dateObj = new Date(nextApt.date_heure);
                                const dayStr = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                                const hourStr = dateObj.getHours() + 'h' + (dateObj.getMinutes() > 0 ? dateObj.getMinutes() : '00');
                                
                                return (
                                    <div className="next-apt-focus-banner fade-in">
                                        <div className="focus-content">
                                            <div className="focus-badge-premium">
                                                <span className="pulse-dot"></span> PROCHAIN RDV
                                            </div>
                                            <div className="focus-main-info">
                                                <div className="focus-date-block">
                                                    <span className="countdown-text">{getCountdown(nextApt.date_heure)}</span>
                                                    <h2 className="focus-date-title">{dayStr} <span className="at-symbol">à</span> {hourStr}</h2>
                                                </div>
                                                <div className="focus-service-details">
                                                    <p className="focus-prestation"><FaTools /> {nextApt.prestation}</p>
                                                    <p className="focus-location"><FaMapMarkerAlt /> {nextApt.adresse || 'Lieu à confirmer'}</p>
                                                </div>
                                            </div>
                                            <div className="focus-actions">
                                                <button className="btn-focus-action primary" onClick={() => setActiveTab('appointments')}>
                                                    Voir les détails <FaChevronRight />
                                                </button>
                                                {nextApt.lat && nextApt.lon && (
                                                    <a 
                                                        href={`https://www.google.com/maps/search/?api=1&query=${nextApt.lat},${nextApt.lon}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn-focus-action secondary"
                                                    >
                                                        <FaMapMarkerAlt /> Itinéraire
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        <div className="focus-visual-decorator">
                                            <FaCalendarAlt />
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* LIVE TRACKER (Original Logic) */}
                            {confirmed.length > 0 && confirmed.find(a => new Date(a.date_heure).toDateString() === new Date().toDateString()) && (
                                <div className="live-service-tracker">
                                    <div className="tracker-header">
                                        <span className="live-dot"></span>
                                        <h3>⚡ Prestation en cours</h3>
                                    </div>
                                    <div className="tracker-steps-container">
                                        {["Démantèlement", "Aspiration", "Shampouinage", "Séchage", "Finitions"].map((step, idx, arr) => {
                                            const activeApt = confirmed.find(a => new Date(a.date_heure).toDateString() === new Date().toDateString());
                                            const currentStepIdx = arr.indexOf(activeApt.suivi_etape);
                                            const isCompleted = idx < currentStepIdx;
                                            const isCurrent = idx === currentStepIdx;
                                            return (
                                                <div key={step} className={`tracker-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                                                    <div className="step-circle">{isCompleted ? '✓' : idx + 1}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Summary Stats & Quick Actions */}
                            <div className="dashboard-summary-layout">
                                <div className="stats-grid-premium">
                                    <div className="stat-card-premium">
                                        <div className="stat-icon-bg blue"><FaCar /></div>
                                        <div className="stat-info">
                                            <span className="val">{history.length}</span>
                                            <span className="lbl">Prestations</span>
                                        </div>
                                    </div>
                                    <div className="stat-card-premium">
                                        <div className="stat-icon-bg green"><FaShieldAlt /></div>
                                        <div className="stat-info">
                                            <span className="val">{vehicles.length}</span>
                                            <span className="lbl">Garage</span>
                                        </div>
                                    </div>
                                    <div className="stat-card-premium">
                                        <div className="stat-icon-bg gold"><FaStar /></div>
                                        <div className="stat-info">
                                            <span className="val">{user?.points || 0}</span>
                                            <span className="lbl">Points Fidélité</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="quick-actions-section">
                                    <h3>Actions Rapides</h3>
                                    <div className="actions-buttons-grid">
                                        <button className="btn-action-tile primary" onClick={() => navigate('/reservation')}>
                                            <div className="tile-icon"><FaCalendarPlus /></div>
                                            <span>Réserver un nettoyage</span>
                                        </button>
                                        <button className="btn-action-tile secondary" onClick={() => setActiveTab('garage')}>
                                            <div className="tile-icon"><FaCar /></div>
                                            <span>Gérer mon garage</span>
                                        </button>
                                        <button className="btn-action-tile secondary" onClick={() => setActiveTab('appointments')}>
                                            <div className="tile-icon"><FaHistory /></div>
                                            <span>Voir historique</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* APPOINTMENTS TAB */}
                    {activeTab === 'appointments' && (
                        <div className="tab-section appointments-tab fade-in">
                            <div className="section-header-row premium">
                                <div className="section-title-with-count">
                                    <h2>Mes Rendez-vous</h2>
                                    <span className="count-pill">{appointments.length}</span>
                                </div>
                                <div className="header-actions">
                                    {garageAppointmentsFilter && (
                                        <button className="clear-filter-btn" onClick={() => setGarageAppointmentsFilter(null)}>
                                            Filtre : {garageAppointmentsFilter} <FaTimes />
                                        </button>
                                    )}
                                    <button className="book-appointment-btn premium" onClick={() => navigate('/reservation')}>
                                        <FaPlus /> <span>Prendre RDV</span>
                                    </button>
                                </div>
                            </div>
                            <div className="dashboard-grid">
                                <div className="dashboard-section">
                                    <div className="section-title-with-count">
                                        <h3><FaHourglassHalf /> En Attente</h3>
                                        <span className="count-pill">{pending.length}</span>
                                    </div>
                                    <div className="apt-list">
                                        {pending.length > 0 ? pending.map(renderCard) : (
                                            <div className="empty-state-mini">
                                                <FaHourglassHalf />
                                                <p>Aucun RDV en attente</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="dashboard-section">
                                    <div className="section-title-with-count">
                                        <h3><FaCheckCircle /> Validés</h3>
                                        <span className="count-pill green">{confirmed.length}</span>
                                    </div>
                                    <div className="apt-list">
                                        {confirmed.length > 0 ? confirmed.map(renderCard) : (
                                            <div className="empty-state-mini">
                                                <FaCheckCircle />
                                                <p>Aucun RDV validé</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="dashboard-section">
                                    <div className="section-title-with-count">
                                        <h3><FaHistory /> Historique</h3>
                                        <span className="count-pill grey">{history.length}</span>
                                    </div>
                                    <div className="apt-list">
                                        {(() => {
                                            const filteredHistory = history.filter(apt => {
                                                if (!garageAppointmentsFilter) return true;
                                                const cleanPlaque = garageAppointmentsFilter.replace(/\s|-/g, '').toUpperCase();
                                                const aptPlaque = apt.options?.match(/\[Plaque:\s?([^\]]+)\]/i)?.[1]?.replace(/\s|-/g, '').toUpperCase();
                                                return aptPlaque === cleanPlaque;
                                            });
                                            return filteredHistory.length > 0 ? filteredHistory.map(renderCard) : (
                                                <div className="empty-state-mini">
                                                    <FaHistory />
                                                    <p>Aucun historique trouvé</p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* GARAGE TAB */}
                    {activeTab === 'garage' && (
                        <div className="tab-section garage-tab fade-in">
                            <div className="section-header-row premium">
                                <div className="section-title-with-count">
                                    <h2><FaCar /> Mon Garage</h2>
                                    <span className="count-pill">{vehicles.length}</span>
                                </div>
                                <button className="add-vehicle-btn premium" onClick={() => setIsAddingVehicle(!isAddingVehicle)}>
                                    <FaPlus /> {isAddingVehicle ? 'Fermer' : 'Ajouter un véhicule'}
                                </button>
                            </div>

                            {isAddingVehicle && (
                                <div className="add-vehicle-container fade-in">
                                    <form onSubmit={handleAddVehicle} className="add-vehicle-form premium compact">
                                        <div className="form-grid-premium">
                                            <div className="form-group-premium">
                                                <label>Marque & Modèle</label>
                                                <div className="input-row">
                                                    <input type="text" placeholder="Marque (ex: Tesla)" value={newVehicle.marque} onChange={e => setNewVehicle({ ...newVehicle, marque: e.target.value })} required />
                                                    <input type="text" placeholder="Modèle (ex: Model 3)" value={newVehicle.modele} onChange={e => setNewVehicle({ ...newVehicle, modele: e.target.value })} required />
                                                </div>
                                            </div>
                                            <div className="form-group-premium">
                                                <label>Catégorie</label>
                                                <select value={newVehicle.categorie} onChange={e => setNewVehicle({ ...newVehicle, categorie: e.target.value })}>
                                                    <option value="citadine">🚗 Citadine</option>
                                                    <option value="berline">🚘 Berline</option>
                                                    <option value="suv">🚙 SUV / Monospace</option>
                                                </select>
                                            </div>
                                            <div className="form-group-premium">
                                                <label>Immatriculation</label>
                                                <div className="plaque-input-siv-wrapper">
                                                    <div className="plaque-eu"></div>
                                                    <input 
                                                        type="text" 
                                                        placeholder="AA-123-AA" 
                                                        value={newVehicle.immatriculation} 
                                                        onChange={e => setNewVehicle({ ...newVehicle, immatriculation: formatPlaque(e.target.value) })} 
                                                        maxLength="9"
                                                        required 
                                                    />
                                                    <div className="plaque-dept"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <button type="submit" className="save-btn-premium-full">
                                            <FaPlus /> AJOUTER AU SHOWROOM
                                        </button>
                                    </form>
                                </div>
                            )}

                            <div className="vehicles-grid">
                                {vehicles.length > 0 ? vehicles.map(v => (
                                    <div key={v.id} className={`vehicle-showroom-card ${v.categorie}`}>
                                        <div className="vehicle-card-header-premium">
                                            <div className={`category-badge-premium ${v.categorie}`}>
                                                {v.categorie === 'citadine' && <><FaCar /> Citadine</>}
                                                {v.categorie === 'berline' && <><FaCar /> Berline</>}
                                                {v.categorie === 'suv' && <><FaCar /> SUV / XL</>}
                                            </div>
                                            <button onClick={() => handleDeleteVehicle(v.id)} className="delete-vehicle-minimal" title="Retirer">
                                                <FaTrash />
                                            </button>
                                        </div>

                                        <div className="vehicle-card-body-premium">
                                            {editingVehicleId === v.id ? (
                                                <div className="inline-edit-form fade-in">
                                                    <div className="input-group-edit">
                                                        <input type="text" value={editVehicleData.marque} onChange={e => setEditVehicleData({...editVehicleData, marque: e.target.value})} placeholder="Marque" />
                                                        <input type="text" value={editVehicleData.modele} onChange={e => setEditVehicleData({...editVehicleData, modele: e.target.value})} placeholder="Modèle" />
                                                    </div>
                                                    <div className="plaque-input-siv-wrapper small">
                                                        <div className="plaque-eu"></div>
                                                        <input 
                                                            type="text" 
                                                            value={editVehicleData.immatriculation} 
                                                            onChange={e => setEditVehicleData({...editVehicleData, immatriculation: formatPlaque(e.target.value)})}
                                                            maxLength="9"
                                                        />
                                                        <div className="plaque-dept"></div>
                                                    </div>
                                                    <div className="edit-actions">
                                                        <button onClick={() => handleSaveEditVehicle(v.id)} className="btn-confirm-edit">OK</button>
                                                        <button onClick={() => setEditingVehicleId(null)} className="btn-cancel-edit">×</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="vehicle-brand-info">
                                                        <div className="brand-row">
                                                            <h4>{v.marque}</h4>
                                                            <button className="btn-edit-minimal" onClick={() => handleStartEditVehicle(v)}><FaEdit /></button>
                                                        </div>
                                                        <p>{v.modele}</p>
                                                    </div>

                                                    <div className="plaque-siv-display">
                                                        <div className="siv-strip-eu"></div>
                                                        <div className="siv-number">{v.immatriculation}</div>
                                                        <div className="siv-strip-dept"><span>75</span></div>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div className="vehicle-card-stats-premium">
                                            {(() => {
                                                const stats = getVehicleStats(v.immatriculation);
                                                return (
                                                    <>
                                                        <div className="stat-pill-premium">
                                                            <span className="stat-label">Lavages effectuées</span>
                                                            <span className="stat-value">{stats.count}</span>
                                                        </div>
                                                        <div className="stat-pill-premium">
                                                            <span className="stat-label">Dernier passage</span>
                                                            <span className="stat-value">
                                                                {stats.lastDate ? new Date(stats.lastDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'Jamais'}
                                                            </span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        <div className="vehicle-card-actions-premium">
                                            <button 
                                                className="btn-garage-action primary reserve" 
                                                onClick={() => navigate('/reservation', { 
                                                    state: { 
                                                        category: 'voiture', 
                                                        type: v.categorie === 'citadine' ? 'Citadine' : (v.categorie === 'berline' ? 'Berline' : 'SUV / Monospace'), 
                                                        plaque: v.immatriculation 
                                                    } 
                                                })}
                                            >
                                                <FaCalendarPlus /> Lancer un nettoyage
                                            </button>
                                            <button 
                                                className="btn-garage-action history" 
                                                onClick={() => {
                                                    setGarageAppointmentsFilter(v.immatriculation);
                                                    setActiveTab('appointments');
                                                }}
                                            >
                                                <FaHistory /> Historique
                                            </button>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-state-large">
                                        <div className="empty-icon-circle">
                                            <FaCar />
                                        </div>
                                        <h3>Votre garage est vide</h3>
                                        <p>Ajoutez vos véhicules pour simplifier vos futures réservations.</p>
                                        <button className="add-vehicle-btn premium" onClick={() => setIsAddingVehicle(true)}>
                                            <FaPlus /> Ajouter mon premier véhicule
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}


                    {/* DOCUMENTS TAB */}
                    {activeTab === 'documents' && (
                        <div className="tab-section documents-tab fade-in">
                            <div className="section-title-with-count">
                                <h2><FaFileInvoice /> Mes Documents</h2>
                                <span className="count-pill">{history.filter(apt => apt.facture).length}</span>
                            </div>

                            <div className="documents-grid">
                                {history.filter(apt => apt.facture).length > 0 ? history.filter(apt => apt.facture).map(apt => (
                                    <div key={apt.id} className="document-premium-card">
                                        <div className="doc-visual">
                                            <div className="doc-icon-circle">
                                                <FaFileInvoice />
                                            </div>
                                            <div className="doc-main-info">
                                                <h4>Facture de prestation</h4>
                                                <p>Réalisée le {new Date(apt.date_heure).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                            </div>
                                        </div>
                                        <div className="doc-actions">
                                            <a href={getPhotoUrl(apt, apt.facture)} className="doc-download-btn" target="_blank" rel="noreferrer" download>
                                                <FaDownload /> PDF
                                            </a>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-state-large">
                                        <div className="empty-icon-circle">
                                            <FaFileInvoice />
                                        </div>
                                        <h3>Aucun document disponible</h3>
                                        <p>Vos factures apparaîtront ici une fois vos prestations terminées.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PROFILE TAB */}
                    {activeTab === 'profile' && (
                        <div className="tab-section profile-tab fade-in">
                            <h2>Mon Profil</h2>
                            <div className="profile-header">
                                {isEditing ? (
                                    <form onSubmit={handleUpdateProfile} className="edit-profile-form">
                                        <div className="form-group-profile">
                                            <label>Nom</label>
                                            <input type="text" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} placeholder="Nom" />
                                        </div>
                                        <div className="form-group-profile">
                                            <label>Téléphone</label>
                                            <input type="tel" value={editData.telephone} onChange={e => setEditData({ ...editData, telephone: e.target.value })} placeholder="Téléphone" />
                                        </div>
                                        <div className="edit-actions">
                                            <button type="submit" className="save-btn">Sauver</button>
                                            <button type="button" onClick={() => setIsEditing(false)} className="cancel-btn">Annuler</button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="profile-info-display">
                                        <div className="profile-card-mini">
                                            <div className="profile-avatar-mini">{user.name ? user.name[0].toUpperCase() : 'C'}</div>
                                            <div>
                                                <h3>{user.name || 'Client'}</h3>
                                                <p>{user.email}</p>
                                                {user.telephone && <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>📞 {user.telephone}</p>}
                                            </div>
                                        </div>
                                        <button className="edit-btn" onClick={() => setIsEditing(true)}><FaEdit /> Modifier mon profil</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Photo Modal */}
            {selectedPhotoApt && (
                <div className="photo-modal-overlay" onClick={() => setSelectedPhotoApt(null)}>
                    <div className="photo-modal-content" onClick={e => e.stopPropagation()}>
                        <h3>✨ Transformation & Rapport</h3>

                        {/* Legal Report */}
                        {selectedPhotoApt.rapport_defauts && (
                            <div className="modal-report-section">
                                <h4><FaExclamationTriangle style={{ color: '#f59e0b' }} /> État des lieux (Avant)</h4>
                                <p className="small-text">Défauts relevés et validés avant l'intervention :</p>
                                <div className="inspector-container-modal">
                                    <VehicleInspector data={selectedPhotoApt.rapport_defauts} readOnly={true} />
                                </div>
                                {selectedPhotoApt.rapport_signature && (
                                    <div className="report-signature-display">
                                        <small>Signé numériquement par le client</small><br />
                                        <img src={selectedPhotoApt.rapport_signature} alt="Signé" />
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedPhotoApt.photo_avant && selectedPhotoApt.photo_apres && (
                            <div className="modal-comparison-section">
                                <div className="comparison-header">
                                    <h4>📸 Comparaison Avant / Après</h4>
                                    <div className="comparison-controls">
                                        <button
                                            className={`mode-btn ${compareMode === 'slider' ? 'active' : ''}`}
                                            onClick={() => setCompareMode('slider')}
                                        >
                                            <FaArrowsAltH /> Slider
                                        </button>
                                        <button
                                            className={`mode-btn ${compareMode === 'grid' ? 'active' : ''}`}
                                            onClick={() => setCompareMode('grid')}
                                        >
                                            <FaColumns /> Grille
                                        </button>
                                        <button className="download-report-btn" onClick={() => generatePhotoReport(selectedPhotoApt)}>
                                            <FaDownload /> Rapport PDF
                                        </button>
                                    </div>
                                </div>

                                {(() => {
                                    const avants = Array.isArray(selectedPhotoApt.photo_avant) ? selectedPhotoApt.photo_avant : [selectedPhotoApt.photo_avant];
                                    const apres = Array.isArray(selectedPhotoApt.photo_apres) ? selectedPhotoApt.photo_apres : [selectedPhotoApt.photo_apres];
                                    const maxPairs = Math.min(avants.length, apres.length);
                                    
                                    // Use state for current index if we have multiple
                                    return (
                                        <div className="multi-pairs-viewer">
                                            {maxPairs > 1 && (
                                                <div className="pairs-tabs">
                                                    {avants.slice(0, maxPairs).map((_, i) => (
                                                        <button 
                                                            key={i} 
                                                            className={`pair-tab ${currentPairIndex === i ? 'active' : ''}`}
                                                            onClick={() => setCurrentPairIndex(i)}
                                                        >
                                                            Vue {i + 1}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {compareMode === 'slider' ? (
                                                <div className="slider-container-modal fade-in">
                                                    <ComparisonSlider
                                                        before={getPhotoUrl(selectedPhotoApt, avants[currentPairIndex] || avants[0])}
                                                        after={getPhotoUrl(selectedPhotoApt, apres[currentPairIndex] || apres[0])}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="comparison-flex grid-view fade-in">
                                                    <div className="comp-img-wrapper">
                                                        <div className="img-tag-avant">ÉTAT INITIAL</div>
                                                        <img src={getPhotoUrl(selectedPhotoApt, avants[currentPairIndex] || avants[0])} alt="Avant" />
                                                    </div>
                                                    <div className="comp-img-wrapper">
                                                        <div className="img-tag-apres">RÉSULTAT FINAL</div>
                                                        <img src={getPhotoUrl(selectedPhotoApt, apres[currentPairIndex] || apres[0])} alt="Après" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {selectedPhotoApt.photos_avant_apres && selectedPhotoApt.photos_avant_apres.length > 0 && (
                            <div className="modal-gallery-section">
                                <h4>📂 Galerie photos complète</h4>
                                <div className="modal-gallery">
                                    {(Array.isArray(selectedPhotoApt.photos_avant_apres) ? selectedPhotoApt.photos_avant_apres : [selectedPhotoApt.photos_avant_apres]).map((photo, index) => (
                                        <img
                                            key={index}
                                            src={getPhotoUrl(selectedPhotoApt, photo)}
                                            alt={`Résultat ${index + 1}`}
                                            className="modal-img"
                                            onClick={() => window.open(getPhotoUrl(selectedPhotoApt, photo), '_blank')}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <button onClick={() => setSelectedPhotoApt(null)} className="close-modal-btn">Fermer l'aperçu</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;
